import React, { useState, useEffect, useRef } from 'react';
import { useMotion } from '../../hooks/useMotion.js';
import { getLenis, reducedMotion } from '../../hooks/useLenis.js';
import { gsap } from 'gsap';
import './Careers.css';

const UNIVERSITY_OPPORTUNITIES = [
  {
    id: 'summer-internship',
    title: 'Summer Internship',
    period: 'May – August',
    type: 'Undergraduate',
    desc: 'Gain hands-on experience in wealth management, work closely with our team on real client projects, and develop your skills through training and mentorship.',
    tags: ['Portfolio Analytics', 'Live Client Mandates', 'Senior Mentorship'],
  },
  {
    id: 'winter-internship',
    title: 'Winter Internship',
    period: 'December – January',
    type: 'Undergraduate',
    desc: 'Engage in short-term projects and gain valuable industry insights, while networking with professionals and enhancing your career prospects.',
    tags: ['Macro Research', 'Custodian Operations', 'Market Insights'],
  },
  {
    id: 'off-cycle-internship',
    title: 'Off-Cycle Internship',
    period: 'Year-round',
    type: 'Flexible Term',
    desc: 'Flexible internship opportunities throughout the year, tailored to fit your academic schedule and career interests.',
    tags: ['Fund Due Diligence', 'Flexible 3–6 Months', 'Advisory Workflows'],
  },
  {
    id: 'graduate-analyst',
    title: 'Graduate Analyst Programme',
    period: 'Annual July Intake',
    type: 'Full-Time Graduate',
    desc: 'Direct-entry pathway for graduating seniors. Rotations across asset allocation, multi-custodian operations, risk compliance, and high-net-worth client advisory.',
    tags: ['Rotational Development', 'Cross-Asset Strategy', 'Licensing Support'],
  },
  {
    id: 'investment-research',
    title: 'Investment Research Fellowship',
    period: 'Academic Semester',
    type: 'Penultimate / Final Year',
    desc: 'Rigorous macro, equity, and alternative asset research. Assist the investment committee in evaluating global markets and thematic portfolio strategies.',
    tags: ['Quantitative Modeling', 'Alternative Assets', 'CIO Briefings'],
  },
  {
    id: 'pwm-apprentice',
    title: 'Private Wealth Management Apprentice',
    period: 'Part-time / Final Year',
    type: 'Graduate Apprenticeship',
    desc: 'Specialized apprenticeship preparing ambitious graduates for high-net-worth relationship management, MAS licensing preparation, and family office advisory dynamics.',
    tags: ['HNW Client Advisory', 'MAS CMS Prep', 'Family Office Dynamics'],
  },
];

export const Careers = () => {
  useMotion();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(UNIVERSITY_OPPORTUNITIES[0]);
  const [statusMessage, setStatusMessage] = useState('');
  const statusRef = useRef(null);

  useEffect(() => {
    document.title = 'Careers | ASTRID FIRST Wealth Advisory';
  }, []);

  // Sync scroll lock with Lenis when modal opens/closes
  useEffect(() => {
    const lenis = getLenis();
    if (isModalOpen) {
      lenis?.stop();
    } else {
      lenis?.start();
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        handleCloseModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      lenis?.start();
    };
  }, [isModalOpen]);

  const handleOpenApply = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setStatusMessage('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleProgramChange = (e) => {
    const found = UNIVERSITY_OPPORTUNITIES.find((opp) => opp.title === e.target.value);
    if (found) {
      setSelectedOpportunity(found);
    } else {
      setSelectedOpportunity({
        id: 'general',
        title: e.target.value,
        period: 'Flexible Term',
        type: 'General Application',
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const msg =
      'This application form is not connected yet. Please send your resume to info@astridfirst.com or call (+65) 6921 7700.';
    setStatusMessage(msg);

    if (!reducedMotion() && statusRef.current) {
      gsap.fromTo(
        statusRef.current,
        { opacity: 0, y: -6 },
        { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' }
      );
    }
  };

  return (
    <div className="careers-page">
      <header className="page-head">
        <div className="page-head-inner">
          <p className="crumb">
            <a href="/">Home</a> / Careers
          </p>
          <h1 data-reveal="chars">Careers</h1>
          <p className="lead" data-reveal="fade">
            We are constantly looking for talented people to join our team.
          </p>
        </div>
      </header>

      <div className="spine">
        <section className="section shell">
          <div className="track">
            <div className="track-head track-head--sticky">
              <p className="eyebrow">Working here</p>
              <h2 data-reveal="lines">Careers at ASTRID FIRST Wealth Advisory</h2>
            </div>
            <div className="prose">
              <p className="lead" data-reveal="fade">
                If you are interested in being part of a firm committed to
                placing clients at the heart of the business, send your resume to{' '}
                <a
                  href="mailto:info@astridfirst.com"
                  style={{ boxShadow: 'inset 0 -1px 0 currentColor' }}
                >
                  info@astridfirst.com
                </a>
                .
              </p>
              <p data-reveal="fade">All qualified candidates will be contacted.</p>
            </div>
          </div>
        </section>

        <section className="section shell" id="university-opportunities">
          <div className="track">
            <div className="track-head track-head--sticky">
              <p className="eyebrow">For students</p>
              <h2 data-reveal="lines">University Opportunities</h2>
            </div>
            <div>
              <div className="opportunities-grid" data-reveal="group">
                {UNIVERSITY_OPPORTUNITIES.map((opp) => (
                  <div key={opp.id} className="opportunity-card cell">
                    <div>
                      <div className="opp-card-header">
                        <span className="opp-period">{opp.period}</span>
                        <span className="opp-type-badge">{opp.type}</span>
                      </div>
                      <h3 className="opp-title">{opp.title}</h3>
                      <p className="opp-desc">{opp.desc}</p>
                    </div>

                    <div>
                      <div className="opp-tags">
                        {opp.tags.map((tag) => (
                          <span key={tag} className="opp-tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="opp-apply-btn"
                        onClick={() => handleOpenApply(opp)}
                        aria-label={`Apply now for ${opp.title}`}
                      >
                        Apply now
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 16 16"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M3 8h10M9 4l4 4-4 4"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="opportunities-cta" data-reveal="fade">
                <div className="opportunities-cta-inner">
                  <div>
                    <h3>Looking for a tailored arrangement?</h3>
                    <p>
                      We welcome direct expressions of interest for research attachments and off-cycle university opportunities.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn"
                    onClick={() =>
                      handleOpenApply({
                        id: 'general',
                        title: 'General University Application',
                        period: 'Flexible Term',
                        type: 'General Expression',
                      })
                    }
                  >
                    Apply now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Application Form Modal */}
      <div
        className={`opp-modal-overlay ${isModalOpen ? 'is-open' : ''}`}
        aria-hidden={!isModalOpen}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleCloseModal();
          }
        }}
      >
        <div
          className="opp-modal-dialog"
          // Lenis captures wheel events page-wide; without this the dialog's own
          // scroll area could not be scrolled with a mouse wheel or trackpad.
          data-lenis-prevent
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-application-title"
        >
          <button
            type="button"
            className="opp-modal-close"
            onClick={handleCloseModal}
            aria-label="Close application form"
          >
            &times;
          </button>

          <div className="opp-modal-header">
            <p className="opp-modal-eyebrow">University Opportunities</p>
            <h2 id="modal-application-title" className="opp-modal-title">
              Apply for {selectedOpportunity.title}
            </h2>
            <span className="opp-modal-badge">{selectedOpportunity.period}</span>
          </div>

          <form
            className="opp-modal-form"
            data-form
            onSubmit={handleSubmit}
          >
            <div className="field form-wide">
              <label htmlFor="opp-program">Program applied for</label>
              <div className="opp-select-wrap">
                <select
                  id="opp-program"
                  name="program"
                  value={selectedOpportunity.title}
                  onChange={handleProgramChange}
                >
                  {UNIVERSITY_OPPORTUNITIES.map((opp) => (
                    <option key={opp.id} value={opp.title}>
                      {opp.title} ({opp.period})
                    </option>
                  ))}
                  <option value="General University Application">
                    General University Application (Flexible Term)
                  </option>
                </select>
              </div>
            </div>

            <div className="field">
              <label htmlFor="opp-first-name">First name</label>
              <input
                id="opp-first-name"
                name="firstName"
                type="text"
                autoComplete="given-name"
                required
              />
            </div>

            <div className="field">
              <label htmlFor="opp-last-name">Last name</label>
              <input
                id="opp-last-name"
                name="lastName"
                type="text"
                autoComplete="family-name"
                required
              />
            </div>

            <div className="field">
              <label htmlFor="opp-email">Email address</label>
              <input
                id="opp-email"
                name="email"
                type="email"
                autoComplete="email"
                required
              />
            </div>

            <div className="field">
              <label htmlFor="opp-phone">Phone number</label>
              <input
                id="opp-phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                required
              />
            </div>

            <div className="field">
              <label htmlFor="opp-university">University / Institution</label>
              <input
                id="opp-university"
                name="university"
                type="text"
                placeholder="e.g. NUS, NTU, SMU, LSE"
                required
              />
            </div>

            <div className="field">
              <label htmlFor="opp-degree">Degree & Major</label>
              <input
                id="opp-degree"
                name="degree"
                type="text"
                placeholder="e.g. B.Sc. Finance / Economics"
                required
              />
            </div>

            <div className="field">
              <label htmlFor="opp-graduation">Expected graduation</label>
              <input
                id="opp-graduation"
                name="graduationYear"
                type="text"
                placeholder="e.g. 2026 / 2027"
              />
            </div>

            <div className="field">
              <label htmlFor="opp-availability">Earliest start date</label>
              <input
                id="opp-availability"
                name="availability"
                type="text"
                placeholder="e.g. May 2026"
              />
            </div>

            <div className="field form-wide">
              <label htmlFor="opp-resume-link">Resume / LinkedIn / Portfolio URL</label>
              <input
                id="opp-resume-link"
                name="resumeUrl"
                type="url"
                placeholder="https://linkedin.com/in/... or drive link"
                required
              />
            </div>

            <div className="field form-wide">
              <label htmlFor="opp-message">Personal note / Message</label>
              <textarea
                id="opp-message"
                name="message"
                rows={4}
                placeholder="Briefly share your background and interest in ASTRID FIRST Wealth Advisory..."
              />
            </div>

            <div className="field form-wide">
              <button className="btn opp-submit-btn" type="submit">
                Submit application
              </button>
              <p
                ref={statusRef}
                data-form-status
                role="status"
                style={{ marginTop: '1rem', fontSize: '0.9rem', color: 'var(--brand-deep)' }}
              >
                {statusMessage}
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};